/**
 * usePendingPayment — checks for recent unseen payments on app open.
 * Returns payment info for modal display.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@turnifly/shared/api'

const LS_KEY_PREFIX = 'seen_payment_'
const LS_LAST_PAYMENT = 'last_payment_id'

export interface PendingPayment {
  id: string
  type: string
  status: string
  amount: number
  metadata: Record<string, unknown>
}

function markSeen(paymentId: string): void {
  localStorage.setItem(`${LS_KEY_PREFIX}${paymentId}`, 'true')
}

function isSeen(paymentId: string): boolean {
  return localStorage.getItem(`${LS_KEY_PREFIX}${paymentId}`) === 'true'
}

export function saveLastPaymentId(paymentId: string): void {
  localStorage.setItem(LS_LAST_PAYMENT, paymentId)
}

export function usePendingPayment(userId: string | undefined) {
  const [payment, setPayment] = useState<PendingPayment | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  // Check for pending payment on mount
  useEffect(() => {
    if (!userId) return

    const check = async (): Promise<void> => {
      // First check localStorage for last payment ID
      const lastId = localStorage.getItem(LS_LAST_PAYMENT)

      if (lastId && !isSeen(lastId)) {
        const { data } = await supabase
          .from('payments')
          .select('id, type, status, amount, metadata')
          .eq('id', lastId)
          .single()
          .throwOnError()

        if (data) {
          const p = data as PendingPayment
          if (p.status !== 'pending') {
            setPayment(p)
            setIsOpen(true)
            return
          }
        }
      }

      // Fallback: check recent payments from DB
      const { data } = await supabase
        .from('payments')
        .select('id, type, status, amount, metadata')
        .eq('user_id', userId)
        .in('status', ['completed', 'failed', 'processing'])
        .gte('created_at', new Date(Date.now() - 10 * 60_000).toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .throwOnError()

      if (data && data.length > 0) {
        const p = data[0] as PendingPayment
        if (!isSeen(p.id)) {
          setPayment(p)
          setIsOpen(true)
        }
      }
    }

    void check()
  }, [userId])

  const close = useCallback((): void => {
    if (payment) {
      markSeen(payment.id)
      localStorage.removeItem(LS_LAST_PAYMENT)
    }
    setIsOpen(false)
    setPayment(null)
  }, [payment])

  const updatePayment = useCallback((updated: PendingPayment): void => {
    setPayment(updated)
  }, [])

  return { payment, isOpen, close, updatePayment }
}
